// @view/payment/SquareCardForm.tsx

import React from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";

import { Card, CardContent, CardTitle } from "@components/ui/card";
import * as styles from "@styles/Payment.css";
import { getSquareSdkState } from "@services/payments/adapters/SquareSdkAdapter";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import { ok } from "@ps-bridge/ok";
import EmptyView from "@view/EmptyView";

export type SquareBillingContact = {
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
  addressLines: string[];
  city: string;
  region: string;
  country: string;
  postalCode: string;
};

export type SquareBillingCardInput = {
  sourceId: string;
  verificationToken: OptionTs<string>;
};

type CreditCardWithPostalCodeProps = React.ComponentProps<typeof CreditCard> & {
  postalCode: string;
  render: (PayButton: React.ComponentType<SquarePayButtonProps>) => React.ReactElement;
};

type SquarePayButtonProps = {
  children: React.ReactNode;
  "aria-label": string;
  isLoading: boolean;
  css: { display: string } | Record<string, never>;
};

const CreditCardWithPostalCode =
  CreditCard as React.ComponentType<CreditCardWithPostalCodeProps>;

type SquareTokenPayload =
  | string
  | {
      token: string;
    }
  | {
      detail: {
        token: string;
      };
    };

export type SquareCardFormProps = {
  onTokenize: (token: string) => Ok;
  onError: (message: string) => Ok;
  billingContact: SquareBillingContact;
  isSubmitting: boolean;
  embedded: boolean;
  showPostalCode: boolean;
};

export type SquareBillingCardCaptureProps = {
  onCardInput: (card: OptionTs<SquareBillingCardInput>) => Ok;
  billingContact: SquareBillingContact;
  isSubmitting: boolean;
  embedded: boolean;
  postalCode: string;
  buttonVisibility: "hidden" | "visible";
  tokenizeRequestNonce: number;
};

export const squareBillingCardFromResult = (
  payload: SquareTokenPayload,
): OptionTs<SquareBillingCardInput> =>
  typeof payload === "string"
    ? Option.some({
        sourceId: payload,
        verificationToken: Option.none<string>(),
      })
    : "token" in payload
      ? Option.some({
          sourceId: payload.token,
          verificationToken: Option.none<string>(),
        })
      : Option.some({
          sourceId: payload.detail.token,
          verificationToken: Option.none<string>(),
        });

const SquareCardContainer: React.FC<{
  billingContact: SquareBillingContact;
  isSubmitting: boolean;
  onResult: (card: OptionTs<SquareBillingCardInput>) => Ok;
  embedded: boolean;
  postalCode: string;
  buttonVisibility: "hidden" | "visible";
  tokenizeRequestNonce: number;
}> = ({
  billingContact,
  isSubmitting,
  onResult,
  embedded,
  postalCode,
  buttonVisibility,
  tokenizeRequestNonce,
}) => {
  const rootRef = React.useRef<OptionTs<HTMLDivElement>>(Option.none());
  const previousTokenizeRequestNonce = React.useRef(0);
  const { isLoaded, config } = getSquareSdkState();

  React.useEffect(() => {
    if (!(tokenizeRequestNonce > previousTokenizeRequestNonce.current)) {
      return;
    }

    previousTokenizeRequestNonce.current = tokenizeRequestNonce;

    Option.fold(
      rootRef.current,
      () => ok,
      (rootElement) => {
        rootElement.querySelector<HTMLButtonElement>("#rswp-card-button")?.click();
        return ok;
      },
    );
  }, [tokenizeRequestNonce]);

  const form = Option.fold(
    config,
    () => <EmptyView />,
    (loadedConfig) =>
      ({
        ready: (
          <PaymentForm
            applicationId={loadedConfig.applicationId}
            locationId={loadedConfig.locationId}
            createVerificationDetails={() => ({
              intent: "STORE",
              billingContact,
            })}
            cardTokenizeResponseReceived={(payload: SquareTokenPayload) => onResult(squareBillingCardFromResult(payload))}
          >
            <CreditCardWithPostalCode
              postalCode={postalCode}
              buttonProps={{
                title: "Save Card Information",
                "aria-label": "Save Card Information",
                isLoading: isSubmitting,
              }}
              render={(PayButton: React.ComponentType<SquarePayButtonProps>) => (
                <PayButton
                  aria-label="Save Card Information"
                  isLoading={isSubmitting}
                  css={buttonVisibility === "hidden" ? { display: "none" } : {}}
                >
                  Save Card Information
                </PayButton>
              )}
            />
          </PaymentForm>
        ),
        notReady: <EmptyView />,
      })[isLoaded ? "ready" : "notReady"],
  );

  return (
    <div
      className={embedded ? styles.embeddedContainer : styles.container}
      ref={(element) => {
        rootRef.current = element ? Option.some(element) : Option.none<HTMLDivElement>();
      }}
    >
      <Card className={embedded ? styles.embeddedCard : styles.card}>
        <CardContent>
          <CardTitle className={styles.title}>Payment Information</CardTitle>
          {form}
        </CardContent>
      </Card>
    </div>
  );
};

export const SquareBillingCardCapture: React.FC<SquareBillingCardCaptureProps> = ({
  onCardInput,
  billingContact,
  isSubmitting,
  embedded,
  postalCode,
  buttonVisibility,
  tokenizeRequestNonce,
}) => (
  <SquareCardContainer
    onResult={onCardInput}
    billingContact={billingContact}
    isSubmitting={isSubmitting}
    embedded={embedded}
    postalCode={postalCode}
    buttonVisibility={buttonVisibility}
    tokenizeRequestNonce={tokenizeRequestNonce}
  />
);

const SquareCardForm: React.FC<SquareCardFormProps> = ({
  onTokenize,
  onError,
  billingContact,
  isSubmitting,
  embedded,
  showPostalCode,
}) => (
  <SquareCardContainer
    billingContact={billingContact}
    isSubmitting={isSubmitting}
    embedded={embedded}
    postalCode={showPostalCode ? billingContact.postalCode : ""}
    buttonVisibility="visible"
    tokenizeRequestNonce={0}
    onResult={(card) =>
      Option.fold(
        card,
        () => onError("Failed to tokenize card."),
        (value) => onTokenize(value.sourceId),
      )
    }
  />
);

export default SquareCardForm;
